import React from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

// Import modules from this directory
import css from './BiddingForm.module.css';
import { getBidValidators } from '../../../util/validators';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import appSettings from '../../../config/settings';
import { propTypes } from '../../../util/types';
import FieldCurrencyInput from '../../FieldCurrencyInput/FieldCurrencyInput';
import Button from '../../Button/Button';
import Form from '../../Form/Form';
import { H3 } from '../../Heading/Heading';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { formatMoney } from '../../../util/currency';
import {
  TIMED_SALE_BUYER_COMMISSION_PERCENTAGE,
  formatBidMoney,
  getTimedSaleFeeBreakdown,
  multiplyMoneyByQuantity,
} from '../../../util/bidPricing';
const { Money } = sdkTypes;

export const BiddingFormComponent = props => (
  <FinalForm
    {...props}
    render={formRenderProps => {
      const {
        formId,
        autoFocus,
        className,
        disabled,
        ready,
        handleSubmit,
        marketplaceCurrency,
        listingMinimumPriceSubUnits,
        invalid,
        pristine,
        updateInProgress,
        fetchErrors,
        values,
        highestBid,
        highestBidCurrency,
        listingQuantity,
        bidUnitLabels,
      } = formRenderProps;
      const intl = useIntl();
      const highestBidMoney = new Money(highestBid, highestBidCurrency);
      const highestBidFormatted = formatMoney(intl, highestBidMoney);
      const bidQuantity =
        Number.isInteger(listingQuantity) && listingQuantity > 0 ? listingQuantity : null;
      const bidUnitPriceMoney = values?.biddingPrice;
      const bidTotalMoney = multiplyMoneyByQuantity(bidUnitPriceMoney, bidQuantity);
      const showBidTotal = Boolean(bidQuantity && bidQuantity > 1);
      const bidHammerPrice = bidTotalMoney || bidUnitPriceMoney;
      const timedSaleFees = getTimedSaleFeeBreakdown(bidHammerPrice);
      const bidQuantityUnitLabel =
        bidQuantity === 1 ? bidUnitLabels.singular : bidUnitLabels.plural;
      const formattedBidUnitPrice = formatBidMoney(intl, bidUnitPriceMoney);
      const formattedBidTotal = formatBidMoney(intl, bidTotalMoney);
      const formattedBidHammerPrice = formatBidMoney(intl, bidHammerPrice);
      const formattedBuyerCommission = formatBidMoney(intl, timedSaleFees?.buyerCommission);
      const formattedBuyerTotal = formatBidMoney(intl, timedSaleFees?.buyerTotal);
      const showBidBreakdown = showBidTotal || Boolean(timedSaleFees);
      const priceValidators = getBidValidators(
        listingMinimumPriceSubUnits,
        marketplaceCurrency,
        intl,
        highestBid
      );
      const classes = classNames(css.root, className);
      const submitInProgress = updateInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;
      const { updateListingError, showListingsError } = fetchErrors || {};

      return (
        <Form onSubmit={handleSubmit} className={classes}>
          {updateListingError ? (
            <p className={css.error}>
              <FormattedMessage id="BiddingForm.updateFailed" />
            </p>
          ) : null}
          {showListingsError ? (
            <p className={css.error}>
              <FormattedMessage id="BiddingForm.showListingFailed" />
            </p>
          ) : null}
          {highestBid ? (
            <H3 as="h3" className={css.highestBid}>
              <FormattedMessage
                id="BiddingForm.highestBid"
                values={{ highestBid: highestBidFormatted }}
              />
            </H3>
          ) : null}
          <FieldCurrencyInput
            id={`${formId}biddingPrice`}
            name="biddingPrice"
            className={css.input}
            autoFocus={autoFocus}
            label={intl.formatMessage(
              {
                id: 'OrderPanel.bidPricePerUnitLabel',
                defaultMessage: 'Bid price per {unitLabel}',
              },
              { unitLabel: bidUnitLabels.singular }
            )}
            placeholder={intl.formatMessage({ id: 'BiddingForm.biddingPricePlaceholder' })}
            currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
            validate={priceValidators}
          />

          {showBidBreakdown ? (
            <div className={css.bidBreakdown} aria-live="polite">
              {showBidTotal ? (
                <>
                  <div className={css.bidBreakdownRow}>
                    <span>
                      <FormattedMessage
                        id="OrderPanel.bidQuantityBreakdownLabel"
                        defaultMessage="Quantity"
                      />
                    </span>
                    <strong>
                      <FormattedMessage
                        id="OrderPanel.bidQuantityBreakdownValue"
                        defaultMessage="{quantity} {unitLabel}"
                        values={{ quantity: bidQuantity, unitLabel: bidQuantityUnitLabel }}
                      />
                    </strong>
                  </div>
                  <div className={css.bidBreakdownRow}>
                    <span>
                      <FormattedMessage
                        id="OrderPanel.bidUnitPriceBreakdownLabel"
                        defaultMessage="Bid price per {unitLabel}"
                        values={{ unitLabel: bidUnitLabels.singular }}
                      />
                    </span>
                    <strong>{formattedBidUnitPrice}</strong>
                  </div>
                </>
              ) : null}
              <div
                className={classNames(css.bidBreakdownRow, {
                  [css.bidBreakdownTotalRow]: !showBidTotal,
                })}
              >
                <span>
                  <FormattedMessage
                    id={
                      showBidTotal
                        ? 'OrderPanel.bidTotalBreakdownLabel'
                        : 'OrderPanel.auctionHammerPriceLabel'
                    }
                    defaultMessage={showBidTotal ? 'Total bid amount' : 'Hammer price'}
                  />
                </span>
                <strong>
                  {showBidTotal ? (
                    <FormattedMessage
                      id="OrderPanel.bidTotalBreakdownValue"
                      defaultMessage="{unitPrice} x {quantity} = {total}"
                      values={{
                        unitPrice: formattedBidUnitPrice,
                        quantity: bidQuantity,
                        total: formattedBidTotal,
                      }}
                    />
                  ) : (
                    formattedBidHammerPrice
                  )}
                </strong>
              </div>
              {timedSaleFees ? (
                <>
                  <div className={css.bidBreakdownRow}>
                    <span>
                      <FormattedMessage
                        id="OrderPanel.auctionBuyerCommissionLabel"
                        defaultMessage="Buyer commission ({percentage}%)"
                        values={{ percentage: TIMED_SALE_BUYER_COMMISSION_PERCENTAGE }}
                      />
                    </span>
                    <strong>{formattedBuyerCommission}</strong>
                  </div>
                  <div className={classNames(css.bidBreakdownRow, css.bidBreakdownTotalRow)}>
                    <span>
                      <FormattedMessage
                        id="OrderPanel.auctionBuyerTotalLabel"
                        defaultMessage="Estimated buyer total"
                      />
                    </span>
                    <strong>{formattedBuyerTotal}</strong>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
          >
            {intl.formatMessage({ id: 'BiddingForm.submitBid' })}
          </Button>
        </Form>
      );
    }}
  />
);

BiddingFormComponent.defaultProps = {
  fetchErrors: null,
  listingMinimumPriceSubUnits: 0,
  formId: 'BiddingForm',
  listingQuantity: null,
  bidUnitLabels: { singular: 'unit', plural: 'units' },
};

BiddingFormComponent.propTypes = {
  formId: string,
  onSubmit: func.isRequired,
  marketplaceCurrency: string.isRequired,
  listingMinimumPriceSubUnits: number,
  listingQuantity: number,
  bidUnitLabels: shape({
    singular: string.isRequired,
    plural: string.isRequired,
  }),
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(BiddingFormComponent);
